import { Component, inject } from '@angular/core';
import { AsyncPipe, CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Router } from '@angular/router';
import { MenubarModule } from 'primeng/menubar';
import { MenuModule } from 'primeng/menu';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { TieredMenuModule } from 'primeng/tieredmenu';
import { DrawerModule } from 'primeng/drawer';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { MenuItem, MessageService } from 'primeng/api';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { ApolloClientService } from '../../services/apollo-client.service';
import { AuthenticationMutations } from '../../graphql/mutations/authentication.mutation';
import { menubarHeaderDesignToken } from '../../theme/menubar-header.design-token';
import { ThemeService } from '../../services/theme.service';
import { ColorblindService } from '../../services/colorblind.service';
import { AuthService } from '../../services/auth.service';
import { Theme } from '../../enums/theme.enum';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { menuDesignToken } from '../../theme/menu';
import { drawerDesignToken } from '../../theme/drawer';
import { menuUrls } from '../../../environments/environment';

/**
 * Application header (Web Analytics branding, theme, account).
 */
@Component({
  selector: 'app-navigation-header',
  imports: [
    MenubarModule,
    MenuModule,
    ButtonModule,
    AsyncPipe,
    CommonModule,
    FormsModule,
    TooltipModule,
    TieredMenuModule,
    DrawerModule,
    ToggleSwitchModule,
    ProgressSpinnerModule,
  ],
  templateUrl: './navigation-header.component.html',
  styleUrl: './navigation-header.component.scss',
  providers: [ColorblindService],
})
export class NavigationHeaderComponent {
  public menubarHeaderDesignToken = menubarHeaderDesignToken;
  public menuDesignToken = menuDesignToken;
  public drawerDesignToken = drawerDesignToken;
  public themeService = inject(ThemeService);
  public colorblindService = inject(ColorblindService);
  public authService = inject(AuthService);
  private apolloClientService = inject(ApolloClientService);
  private breakpointObserver = inject(BreakpointObserver);
  private router = inject(Router);
  private messageService = inject(MessageService);
  public Theme = Theme;
  public homeUrl = menuUrls.homeUrl;

  public isMobile$ = this.breakpointObserver.observe('(max-width: 960px)').pipe(
    map((result) => result.matches)
  );

  public showSettings = false;
  public showUserDetails = false;

  navigationItems: MenuItem[] = [
    {
      label: 'Dashboard',
      icon: 'pi pi-chart-line',
      command: () => this.router.navigate(['/']),
    },
  ];

  public menuItems$: Observable<MenuItem[]> = this.authService.userData$.pipe(
    map((user) => [
      {
        label: user.firstName + ' ' + user.lastName,
        icon: 'pi pi-user',
        visible: !!user.sessionID,
        command: () => {
          this.showUserDetails = true;
        },
      },
      {
        label: 'Settings',
        icon: 'pi pi-cog',
        command: () => {
          this.showSettings = true;
        },
      },
      {
        icon: 'pi pi-question-circle',
        label: 'Support',
        command: () => {
          window.open(menuUrls.supportUrl, '_blank');
        },
      },
      {
        icon: 'pi pi-home',
        label: 'Test Tools',
        command: () => {
          window.open(menuUrls.homeUrl, '_blank');
        },
      },
      {
        label: user.sessionID ? 'Sign out' : 'Sign in',
        icon: user.sessionID ? 'pi pi-sign-out' : 'pi pi-sign-in',
        command: () => {
          user.sessionID ? this.signOut() : this.authService.login();
        },
      },
    ])
  );

  constructor() {
    this.themeService.getTheme();
    this.colorblindService.getColorblindMode();
  }

  public signOut(): void {
    this.apolloClientService.authLayerClient
      .pipe(
        take(1),
        switchMap((client) =>
          client.mutate<{ signOut: boolean | null }>({
            mutation: AuthenticationMutations.signOut,
          })
        ),
        map((response) => response ?? false),
        tap((response) => {
          if (response) {
            this.messageService.add({
              severity: 'success',
              detail: 'Successfully signed out',
            });
            this.authService.clearSession();
          } else {
            throw new Error('Failed when signing out');
          }
        }),
        catchError((error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error when signing out',
            detail: String(error),
          });
          console.error('Error signing out:', error);
          this.authService.clearSession();
          return of(undefined);
        })
      )
      .subscribe();
  }

  navigateToDashboard(): void {
    this.router.navigate(['/']);
  }
}
